import { Resume, SupabaseResume } from '@applicator/shared'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from './ConfirmDialog'
import CreateResumeModal from './CreateResumeModal'
import { InstantTooltip } from './InstantTooltip'
import { ResumeListTypeFilter } from './ResumeListTypeFilter'
import SearchInput from './SearchInput'
import TemplateResumeBadge from './TemplateResumeBadge'
import TemplateResumeMarkIcon from './TemplateResumeMarkIcon'
import { Table, TableColumn } from './Table'

import { deleteResumesForCurrentUser } from '../lib/deleteResumes'
import { duplicateResume } from '../lib/duplicateResume'
import { getErrorMessage } from '../lib/errorMessage'
import { fetchAccountContactSeed, mergeContactInfoForNewResume } from '../lib/accountContactSeed'
import {
  fetchAccountWorkExperience,
  workExperienceHasContent,
} from '../lib/accountWorkExperience'
import {
  fetchAccountEducation,
  educationHasContent,
  mergeEducationCatalogWithResumeInclusions,
} from '../lib/accountEducation'
import {
  fetchAccountSkills,
  mergeCatalogWithResumeInclusions,
  skillsHasContent,
} from '../lib/accountSkills'
import { RESUME_SECTIONS } from './ResumeDetail/constants'
import { trackResumeCreated } from '../lib/posthogEvents'
import { supabase } from '../lib/supabase'
import { showError, showSuccess } from '../lib/toast'
import { resumeIdsToRowIndices, rowIndicesToResumeIds } from '../utils/resumeListSelection'
import { filterResumeList, getResumeListEmptyMessage } from '../utils/resumeListSearch'
import {
  fetchTemplateResumes,
  setResumeTemplateFlag,
  type TemplateResumeSummary,
} from '../lib/resumeTemplates'

type DeleteConfirmState = {
  isOpen: boolean
  type: 'single' | 'bulk'
  resumeIds: string[]
  message: string
}

type UserProfileRecord = {
  first_name: string | null
  last_name: string | null
  phone: string | null
  email: string | null
}

function DuplicateResumeIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  )
}

function ResumesListSkeleton() {
  return (
    <div
      className="h-full border border-border-default rounded-lg p-4 space-y-3 animate-pulse"
      data-testid="resumes-list-skeleton"
      aria-busy="true"
      aria-label="Loading resumes"
    >
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-10 rounded bg-surface-muted" />
      ))}
    </div>
  )
}

function DeleteResumeIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  )
}

function buildDefaultContactInfo(
  profile: UserProfileRecord | null,
  authEmail: string | undefined
) {
  return {
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: profile?.email || authEmail || '',
    phone: profile?.phone || '',
  }
}

function ResumesList() {
  const navigate = useNavigate()
  const [resumes, setResumes] = useState<Resume[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const [selectedResumeIds, setSelectedResumeIds] = useState<Set<string>>(new Set())
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null)
  const [titleSearchQuery, setTitleSearchQuery] = useState('')
  const [templatesOnly, setTemplatesOnly] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [templateResumes, setTemplateResumes] = useState<TemplateResumeSummary[]>([])
  const [creatingResume, setCreatingResume] = useState(false)
  const [togglingTemplateId, setTogglingTemplateId] = useState<string | null>(null)

  const loadResumes = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setError('User not authenticated')
        return
      }

      const { data, error: fetchError } = await supabase
        .from('resumes')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (fetchError) throw fetchError

      const formattedResumes: Resume[] = (data as SupabaseResume[]).map((r) => ({
        id: r.id,
        title: r.title,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        isTemplate: Boolean(r.is_template),
      }))

      setResumes(formattedResumes)
      setSelectedResumeIds(new Set())
    } catch (err) {
      console.error('Error loading resumes:', err)
      setError(err instanceof Error ? err.message : 'Failed to load resumes')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadResumes()
  }, [loadResumes])

  const loadTemplateResumes = useCallback(async () => {
    try {
      const templates = await fetchTemplateResumes(supabase)
      setTemplateResumes(templates)
    } catch (err) {
      console.error('Error loading template resumes:', err)
    }
  }, [])

  const openCreateModal = useCallback(() => {
    void loadTemplateResumes()
    setCreateModalOpen(true)
  }, [loadTemplateResumes])

  const createBlankResume = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: profileData, error: profileError } = await supabase
        .from('user_profile')
        .select('first_name, last_name, phone, email')
        .eq('user_id', user.id)
        .maybeSingle()

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError
      }

      const { data, error: createError } = await supabase
        .from('resumes')
        .insert({
          user_id: user.id,
          title: 'New Resume',
          is_template: false,
        })
        .select()
        .single()

      if (createError) throw createError

      if (data) {
        const accountSeed = await fetchAccountContactSeed(supabase)
        const defaultContactInfo = mergeContactInfoForNewResume(
          buildDefaultContactInfo(
            (profileData as UserProfileRecord | null) ?? null,
            user.email,
          ),
          accountSeed,
        )

        const { error: contentError } = await supabase
          .from('resume_content')
          .insert({
            resume_id: data.id,
            section_type: 'contact_info',
            content: defaultContactInfo,
            display_order: 0,
          })

        if (contentError) throw contentError

        const accountWorkExperience = await fetchAccountWorkExperience(supabase, {
          excludeResumeId: data.id,
        })
        if (accountWorkExperience && workExperienceHasContent(accountWorkExperience)) {
          const workExperienceDisplayOrder = RESUME_SECTIONS.findIndex(
            (section) => section.type === 'work_experience',
          )
          const { error: workExperienceError } = await supabase.from('resume_content').insert({
            resume_id: data.id,
            section_type: 'work_experience',
            content: accountWorkExperience,
            display_order: workExperienceDisplayOrder,
          })
          if (workExperienceError) throw workExperienceError
        }

        const accountSkills = await fetchAccountSkills(supabase, {
          excludeResumeId: data.id,
        })
        if (accountSkills && skillsHasContent(accountSkills)) {
          const skillsDisplayOrder = RESUME_SECTIONS.findIndex((section) => section.type === 'skills')
          const seededSkills = mergeCatalogWithResumeInclusions(accountSkills, [], {
            defaultIncluded: false,
          })
          const { error: skillsError } = await supabase.from('resume_content').insert({
            resume_id: data.id,
            section_type: 'skills',
            content: seededSkills,
            display_order: skillsDisplayOrder,
          })
          if (skillsError) throw skillsError
        }

        const accountEducation = await fetchAccountEducation(supabase, {
          excludeResumeId: data.id,
        })
        if (accountEducation && educationHasContent(accountEducation)) {
          const educationDisplayOrder = RESUME_SECTIONS.findIndex(
            (section) => section.type === 'education',
          )
          const seededEducation = mergeEducationCatalogWithResumeInclusions(accountEducation, [], {
            defaultIncluded: false,
          })
          const { error: educationError } = await supabase.from('resume_content').insert({
            resume_id: data.id,
            section_type: 'education',
            content: seededEducation,
            display_order: educationDisplayOrder,
          })
          if (educationError) throw educationError
        }

        trackResumeCreated(data.id)
        setCreateModalOpen(false)
        navigate(`/resume/${data.id}`)
      }
    } catch (err) {
      console.error('Error creating resume:', err)
      setError(err instanceof Error ? err.message : 'Failed to create resume')
      showError(err instanceof Error ? err.message : 'Failed to create resume')
    } finally {
      setCreatingResume(false)
    }
  }

  const handleCreateBlankResume = async () => {
    setCreatingResume(true)
    await createBlankResume()
  }

  const handleCreateFromTemplate = async (templateId: string) => {
    try {
      setCreatingResume(true)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        showError('User not authenticated')
        return
      }

      const duplicate = await duplicateResume(supabase, {
        sourceResumeId: templateId,
        userId: user.id,
      })

      trackResumeCreated(duplicate.id)
      setCreateModalOpen(false)
      showSuccess(`Created "${duplicate.title}"`)
      navigate(`/resume/${duplicate.id}`)
    } catch (err) {
      console.error('Error creating resume from template:', err)
      const message = err instanceof Error ? err.message : 'Failed to create resume from template'
      setError(message)
      showError(message)
    } finally {
      setCreatingResume(false)
    }
  }

  const handleToggleTemplate = useCallback(async (resume: Resume) => {
    const next = !resume.isTemplate
    try {
      setTogglingTemplateId(resume.id)
      await setResumeTemplateFlag(supabase, resume.id, next)
      setResumes((prev) =>
        prev.map((r) => (r.id === resume.id ? { ...r, isTemplate: next } : r)),
      )
      showSuccess(next ? 'Marked as template resume' : 'Removed template mark')
      void loadTemplateResumes()
    } catch (err) {
      console.error('Error toggling template flag:', err)
      showError(err instanceof Error ? err.message : 'Failed to update template flag')
    } finally {
      setTogglingTemplateId(null)
    }
  }, [loadTemplateResumes])

  const handleDuplicateResume = useCallback(async (resume: Resume) => {
    try {
      setDuplicatingId(resume.id)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        showError('User not authenticated')
        return
      }

      const duplicate = await duplicateResume(supabase, {
        sourceResumeId: resume.id,
        userId: user.id,
      })

      showSuccess(`Created "${duplicate.title}"`)
      navigate(`/resume/${duplicate.id}`)
    } catch (err) {
      console.error('Error duplicating resume:', err)
      const message = err instanceof Error ? err.message : 'Failed to duplicate resume'
      setError(message)
      showError(message)
    } finally {
      setDuplicatingId(null)
    }
  }, [navigate])

  const requestDeleteResumes = useCallback((resumeIds: string[], type: 'single' | 'bulk') => {
    const uniqueIds = [...new Set(resumeIds.filter(Boolean))]
    if (uniqueIds.length === 0) return

    const titles = uniqueIds
      .map((id) => resumes.find((r) => r.id === id)?.title)
      .filter(Boolean) as string[]

    const isTemplateDelete =
      type === 'single' &&
      uniqueIds.length === 1 &&
      Boolean(resumes.find((r) => r.id === uniqueIds[0])?.isTemplate)

    const message =
      type === 'single' && titles[0]
        ? `Delete "${titles[0]}"?${isTemplateDelete ? ' This is a template resume.' : ''} This cannot be undone.`
        : `Delete ${uniqueIds.length} resume(s)? This cannot be undone.`

    setDeleteConfirm({
      isOpen: true,
      type,
      resumeIds: uniqueIds,
      message,
    })
  }, [resumes])

  const handleDeleteResume = useCallback((resume: Resume) => {
    requestDeleteResumes([resume.id], 'single')
  }, [requestDeleteResumes])

  const handleBulkDelete = useCallback((selectedData: Resume[]) => {
    requestDeleteResumes(
      selectedData.map((r) => r.id),
      'bulk',
    )
  }, [requestDeleteResumes])

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm?.resumeIds.length) return

    const idsToDelete = deleteConfirm.resumeIds
    const previousResumes = resumes
    const previousSelection = selectedResumeIds

    setDeleteConfirm(null)
    setDeletingIds(new Set(idsToDelete))
    setResumes((prev) => prev.filter((r) => !idsToDelete.includes(r.id)))
    setSelectedResumeIds(new Set())

    try {
      await deleteResumesForCurrentUser(supabase, idsToDelete)
      const count = idsToDelete.length
      showSuccess(count === 1 ? 'Resume deleted' : `${count} resumes deleted`)
    } catch (err) {
      console.error('Error deleting resume(s):', err)
      const message = getErrorMessage(err, 'Failed to delete resume(s)')
      setResumes(previousResumes)
      setSelectedResumeIds(previousSelection)
      setError(message)
      showError(message)
    } finally {
      setDeletingIds(new Set())
    }
  }, [deleteConfirm, resumes, selectedResumeIds])

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const filteredResumes = useMemo(
    () => filterResumeList(resumes, { titleQuery: titleSearchQuery, templatesOnly }),
    [resumes, titleSearchQuery, templatesOnly],
  )

  const hasTemplateResumes = useMemo(
    () => resumes.some((resume) => resume.isTemplate),
    [resumes],
  )

  const sortedResumes = useMemo(() => {
    if (!sortColumn) return filteredResumes

    const sorted = [...filteredResumes].sort((a, b) => {
    let aValue: string | number
    let bValue: string | number

      switch (sortColumn) {
        case 'title':
          aValue = a.title.toLowerCase()
          bValue = b.title.toLowerCase()
          break
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime()
          bValue = new Date(b.createdAt).getTime()
          break
        case 'updatedAt':
          aValue = new Date(a.updatedAt).getTime()
          bValue = new Date(b.updatedAt).getTime()
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [filteredResumes, sortColumn, sortDirection])

  const selectedResumeData = useMemo(
    () => sortedResumes.filter((resume) => selectedResumeIds.has(resume.id)),
    [sortedResumes, selectedResumeIds],
  )

  const selectedRowIndices = useMemo(
    () => resumeIdsToRowIndices(sortedResumes, selectedResumeIds),
    [sortedResumes, selectedResumeIds],
  )

  const handleRowSelect = useCallback(
    (indices: Set<number>) => {
      setSelectedResumeIds(rowIndicesToResumeIds(sortedResumes, indices))
    },
    [sortedResumes],
  )

  const tableColumns: TableColumn<Resume>[] = useMemo(() => [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      required: true,
      render: (_value, resume) => (
        <span className="text-body text-text-primary font-medium inline-flex items-center gap-2">
          {resume.title}
          {resume.isTemplate && <TemplateResumeBadge />}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (_value, resume) => (
        <span className="text-small text-text-secondary">{formatDateTime(resume.createdAt)}</span>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      render: (_value, resume) => (
        <span className="text-small text-text-secondary">{formatDateTime(resume.updatedAt)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      sticky: 'right',
      width: '148px',
      minWidth: '148px',
      render: (_value, resume) => {
        const isDuplicating = duplicatingId === resume.id
        const isDeleting = deletingIds.has(resume.id)
        const isTogglingTemplate = togglingTemplateId === resume.id
        const actionsDisabled = isDuplicating || isDeleting || isTogglingTemplate

        return (
          <div className="flex items-center justify-center gap-1 w-full h-full px-1">
            <InstantTooltip
              label={resume.isTemplate ? 'Remove template' : 'Use as template'}
            >
              <button
                type="button"
                aria-label={
                  resume.isTemplate
                    ? `Remove template mark from ${resume.title}`
                    : `Use ${resume.title} as template`
                }
                disabled={actionsDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleToggleTemplate(resume)
                }}
                className="w-8 h-8 flex items-center justify-center rounded border border-border-strong text-text-muted hover:text-text-secondary hover:border-border-default hover:bg-surface-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TemplateResumeMarkIcon active={Boolean(resume.isTemplate)} />
              </button>
            </InstantTooltip>
            <InstantTooltip label="Duplicate resume">
              <button
                type="button"
                aria-label={`Duplicate ${resume.title}`}
                disabled={actionsDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleDuplicateResume(resume)
                }}
                className="w-8 h-8 flex items-center justify-center rounded border border-border-strong text-text-muted hover:text-text-secondary hover:border-border-default hover:bg-surface-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DuplicateResumeIcon />
              </button>
            </InstantTooltip>
            <InstantTooltip label="Delete resume">
              <button
                type="button"
                aria-label={`Delete ${resume.title}`}
                disabled={actionsDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteResume(resume)
                }}
                className="w-8 h-8 flex items-center justify-center rounded border border-border-strong text-text-muted hover:text-danger hover:border-danger/30 hover:bg-danger-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DeleteResumeIcon />
              </button>
            </InstantTooltip>
          </div>
        )
      },
    },
  ], [deletingIds, duplicatingId, handleDeleteResume, handleDuplicateResume, handleToggleTemplate, togglingTemplateId])

  const handleRowClick = (resume: Resume) => {
    navigate(`/resume/${resume.id}`)
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6" data-testid="page-resumes">
        <div className="text-danger bg-danger-subtle px-4 py-3 rounded-lg">Error: {error}</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="page-resumes">
      <div className="p-6 pb-0 flex-shrink-0">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <h1 className="text-h1 font-semibold text-text-primary shrink-0">Resumes</h1>
            {!loading && resumes.length > 0 && (
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="max-w-md w-full min-w-[12rem] flex-1">
                  <SearchInput
                    value={titleSearchQuery}
                    onChange={setTitleSearchQuery}
                    placeholder="Search resumes by title..."
                    showClearButton
                    data-testid="resumes-title-search"
                  />
                </div>
                <ResumeListTypeFilter
                  templatesOnly={templatesOnly}
                  onChange={setTemplatesOnly}
                  templatesAvailable={hasTemplateResumes}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {selectedResumeIds.size > 0 && (
              <button
                type="button"
                onClick={() => handleBulkDelete(selectedResumeData)}
                disabled={deletingIds.size > 0}
                className="px-3 py-2 text-sm font-medium text-danger bg-danger-subtle border border-danger/20 rounded-lg hover:bg-danger-subtle disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-h-11"
              >
                <DeleteResumeIcon />
                <span>Delete ({selectedResumeIds.size})</span>
              </button>
            )}
            <button
              onClick={openCreateModal}
              disabled={loading}
              className="px-4 py-2 bg-brand-primary text-text-inverse rounded-lg hover:bg-brand-primary-hover transition-colors min-h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + New Resume
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="h-full border border-border-default rounded-lg">
          {loading ? (
            <ResumesListSkeleton />
          ) : (
          <Table<Resume>
            columns={tableColumns}
            data={sortedResumes}
            loading={false}
            error={error}
            onRowClick={handleRowClick}
            selectable={true}
            selectedRows={selectedRowIndices}
            onRowSelect={handleRowSelect}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            showColumnManagement={true}
            emptyMessage={getResumeListEmptyMessage({
              titleQuery: titleSearchQuery,
              templatesOnly,
            })}
            getRowId={(row) => row.id}
            renderEmptyState={
              resumes.length === 0
                ? () => (
              <div className="flex flex-col items-center justify-center py-12 text-text-muted">
                <svg
                  className="w-16 h-16 mb-4 text-text-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-lg mb-2">No resumes yet</p>
                <p className="text-sm mb-4">Create your first resume to get started</p>
                <button
                  onClick={openCreateModal}
                  className="px-4 py-2 bg-brand-primary text-text-inverse rounded-lg hover:bg-brand-primary-hover transition-colors min-h-11"
                >
                  Create Resume
                </button>
              </div>
                )
                : undefined
            }
          />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm?.isOpen ?? false}
        title={deleteConfirm?.type === 'bulk' ? 'Delete resumes' : 'Delete resume'}
        message={deleteConfirm?.message ?? ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          void confirmDelete()
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      <CreateResumeModal
        isOpen={createModalOpen}
        templateResumes={templateResumes}
        creating={creatingResume}
        onClose={() => {
          if (!creatingResume) setCreateModalOpen(false)
        }}
        onCreateBlank={() => void handleCreateBlankResume()}
        onCreateFromTemplate={(templateId) => void handleCreateFromTemplate(templateId)}
      />
    </div>
  )
}

export default ResumesList
